import React, { useMemo, useState } from 'react'
import { Empty, Input, Modal, Radio, Select, Typography } from 'antd'
import { IcCheck, IcSearch } from 'assets/icons'
import { useQuery } from 'react-query'
import { logout } from 'store/actions'
import { useDispatch, useSelector } from 'react-redux'
import { apiGet } from 'api'
import { Loading, SparepartListFooter } from 'components'
import { SEARCH_DEBOUNCE_MS } from 'constant'
import useDebounce from 'hooks/useDebounce'
import useInfiniteScrollSentinel from 'hooks/useInfiniteScrollSentinel'
import useInfiniteSpareparts from 'hooks/useInfiniteSpareparts'
import { formatCurrency, showErrorMessage } from 'utils'

// Hides spareparts already attached to the unit (`attachedUnits[].sparepart_id`).
const getUnattachedSpareparts = (spareparts, attachedUnits) => {
  const attachedIds = new Set((attachedUnits ?? []).map((unit) => unit?.sparepart_id))
  return spareparts.filter((item) => !attachedIds.has(item?.id))
}

const ModalSparepartUnit = ({ isOpen, onCancel, onSubmit, data }) => {
  const [selectedData, setSelectedData] = useState([])
  const [category, setCategory] = useState()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const dispatch = useDispatch()
  const token = useSelector((state) => state.auth.token)
  const [listCategory, setListCategory] = useState([])

  const categoryData = useQuery({
    queryKey: ['category-list'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await apiGet({
        token,
        url: 'servis-service/admin/sparepart-categories/all',
        onLogout: () => dispatch(logout())
      })
      return res
    },
    onSuccess: (res) => {
      // setSelectedData(data ?? [])
      setSearch('')
      setDebouncedSearch('')
      setListCategory(res?.resData?.data ?? [])
    },
    enabled: isOpen
  })

  useDebounce(() => setDebouncedSearch(search), [search], SEARCH_DEBOUNCE_MS)

  const {
    spareparts,
    isLoading,
    isError,
    isFetchingNextPage,
    canLoadMore,
    fetchNextPage,
    refetch
  } = useInfiniteSpareparts({
    keywords: debouncedSearch,
    categoryId: category,
    isEnabled: isOpen && !categoryData.isLoading
  })
  const sentinelRef = useInfiniteScrollSentinel(canLoadMore, fetchNextPage)

  const availableSpareparts = useMemo(
    () => getUnattachedSpareparts(spareparts, data),
    [spareparts, data]
  )
  const isListLoading = isLoading || categoryData.isLoading
  const isListSettled = !isListLoading && !isError && !isFetchingNextPage
  const isListEmpty = isListSettled && !canLoadMore && availableSpareparts.length === 0
  const hasActiveFilter = Boolean(debouncedSearch || category)

  const handleRetryLoad = () => refetch()

  const onClickitem = (item) => {
    let newArray = [...selectedData]
    let index = newArray.findIndex((unit) => unit?.sparepart_id === item.id)
    if (index >= 0) {
      newArray.splice(index, 1)
    } else {
      newArray.push({
        name: item.name,
        sparepart_id: item.id,
        qty: 0,
        uom: null
      })
    }
    setSelectedData(newArray)
  }

  const onEditSelected = (key, idx, value) => {
    let newArray = [...selectedData]
    if (idx >= 0) {
      newArray[idx] = {
        ...newArray[idx],
        [key]: value
      }
    }
    setSelectedData(newArray)
  }

  return (
    <Modal
      width={400}
      centered
      title={'Pilih Sparepart'}
      open={isOpen}
      onCancel={() => {
        onCancel()
        setSelectedData([])
        setCategory(null)
      }}
      okText={'Confirm'}
      onOk={() => {
        let checkQty = true
        let checkUom = true
        selectedData.map((data) => {
          checkQty = checkQty && data?.qty > 0
          checkUom = checkUom && !!data?.uom
          checkUom = checkUom && data?.uom !== ''
        })
        if (!checkQty) {
          showErrorMessage({ content: 'Data Qty tidak boleh 0' })
          return
        }
        if (!checkUom) {
          showErrorMessage({ content: 'Data Satuan Wajib diisi' })
          return
        }
        setSelectedData([])
        onSubmit(selectedData)
        setCategory(null)
      }}
      okButtonProps={{ className: 'bg-primary-500 rounded-md', type: 'danger' }}
      cancelButtonProps={{
        className: 'hover:text-[#000] rounded-md hover:border-[#000]',
        type: 'default'
      }}>
      <div className="bg-gray-50 p-2 rounded-lg">
        <Select
          showSearch
          className="custom-table-limit"
          placeholder="Pilih Kategori"
          style={{ width: '100%' }}
          value={category}
          // onSearch={e => setSearchCategory(e)}
          onChange={(value) => {
            setCategory(value)
          }}
          filterOption={(input, option) =>
            option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {listCategory?.map((item) => (
            <Select.Option key={item?.id?.toString()} value={item?.id}>
              {item?.name}
            </Select.Option>
          ))}
        </Select>
        <Input
          className="rounded-md py-1.5 mt-2"
          placeholder="Cari Sparepart"
          prefix={<IcSearch className="text-gray-400" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div>
          <div className="bg-gray-50 mt-2 p-2 flex items-center rounded-t-lg border-solid border-gray-200 hover:cursor-pointer">
            <div className="flex-1">
              <Typography.Text className="mx-2 font-semibold">Sparepart</Typography.Text>
            </div>
            <Typography.Text className="mx-4 font-semibold">Harga</Typography.Text>
          </div>
        </div>
        <div className="overflow-y-auto overscroll-y-none h-40 border-solid border-gray-200">
          {isListLoading ? <SparepartListLoading /> : null}
          {availableSpareparts.map((item) => (
            <div
              key={item.id}
              className={`${
                item?.is_general_sparepart
                  ? 'bg-gray-300 hover:cursor-not-allowed'
                  : 'bg-white hover:cursor-pointer'
              } p-2 flex items-center rounded-lg mb-1 border-solid border-white `}
              onClick={() => (item?.is_general_sparepart ? {} : onClickitem(item))}>
              {/* <span className='bg-primary-50 rounded-full flex w-fit items-center justify-center p-2'>
                            </span> */}
              {selectedData.some((e) => e.sparepart_id === item.id) ? (
                <div className="w-4 h-4 rounded-lg bg-primary-900 flex items-center justify-center">
                  <IcCheck className="text-white" size={13} />
                </div>
              ) : (
                <div className="w-4 h-4 rounded-md bg-whites border-solid border-gray-200" />
              )}
              <div className="flex-1">
                <Typography.Text className={`text-gray-400 mx-2 block`}>{item.sku}</Typography.Text>
                <Typography.Text
                  className={`${item?.is_general_sparepart ? 'text-gray-400' : ''} mx-2 block`}>
                  {item.name}
                </Typography.Text>
              </div>
              <Typography.Text
                className={`${
                  item?.is_general_sparepart ? 'text-gray-400' : ''
                } mr-2 ml-4 font-semibold`}>
                {formatCurrency(item?.price ?? '0')}
              </Typography.Text>
            </div>
          ))}
          {isListEmpty ? <SparepartListEmpty hasActiveFilter={hasActiveFilter} /> : null}
          <SparepartListFooter
            sentinelRef={sentinelRef}
            canLoadMore={canLoadMore}
            isFetchingNextPage={isFetchingNextPage}
            isError={isError}
            onRetry={handleRetryLoad}
          />
        </div>
      </div>

      <div>
        {selectedData?.map((data, idx) => (
          <div className="flex justify-between items-center py-2" key={idx.toString()}>
            <div>{data.name}</div>
            <div className="flex items-center">
              <div>Qty: </div>
              <Input
                className="rounded-md py-1 w-10 ml-1 mr-3"
                type="numeric"
                value={data?.qty}
                onChange={(e) => onEditSelected('qty', idx, e.target.value)}
              />
              <Radio.Group
                className="custom-radio-btn"
                name="radiogroup"
                value={data?.uom}
                onChange={(e) => onEditSelected('uom', idx, e.target.value)}>
                <Radio className="block" value={'Pcs'}>
                  Pcs
                </Radio>
                <Radio value={'L'}>Liter</Radio>
              </Radio.Group>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  )
}

function SparepartListLoading() {
  return (
    <div role="status" aria-live="polite" className="h-full">
      <Loading visible title="Memuat sparepart..." />
    </div>
  )
}

function SparepartListEmpty({ hasActiveFilter }) {
  const description = hasActiveFilter
    ? 'Sparepart tidak ditemukan. Coba kata kunci atau kategori lain.'
    : 'Tidak ada sparepart yang bisa ditambahkan.'
  return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />
}

export default ModalSparepartUnit
